/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { NewsMediaCardFragment } from "@cosmos/hygraph-schema";
import {
	CosmosAsset,
	CosmosCard,
	CosmosRichText,
	CosmosText,
	OptimizedCosmosAssetFragment,
} from "@components/elements";
import { AnalyticsProps } from "@components/types";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";

const newsMediaCardContentStyles = css({
	paddingBlockStart: "eds.dimension.24",
	flexGrow: "1",
	justifyContent: "space-between",
});

const newsMediaCardHeadingStyles = css({
	lineClamp: "1",
	textOverflow: "ellipsis",
	overflow: "hidden",
	alignSelf: "start",
	textAlign: "start",
});

const newsMediaCardParagraphStyles = css({
	lineClamp: "3",
	textOverflow: "ellipsis",
	overflow: "hidden",
});

export function NewsMediaCard({
	asset,
	href,
	title,
	desc,
	author,
	analytics,
}: NewsMediaCardFragment & AnalyticsProps): React.JSX.Element {
	const sizedAsset: OptimizedCosmosAssetFragment = {
		...(asset || {}),
		id: asset?.id || "",
		url: asset?.url || "",
		width: 392,
		height: 221,
	};

	return (
		<CosmosCard padding={16} variant="material" className="news-media-card">
			<CosmosCard.Thumbnail
				aspectRatio="16/9"
				className={css({ height: "auto", minHeight: "fit-content", width: "100%" })}
			>
				<CosmosAsset asset={sizedAsset} />
			</CosmosCard.Thumbnail>
			<CosmosCard.Content className={newsMediaCardContentStyles}>
				<VStack alignItems="stretch" gap="eds.dimension.24" justifyContent="space-between" h="100%">
					<VStack alignItems="start" gap="eds.dimension.8">
						<CosmosCard.Link href={href || ""} analytics={analytics} className={newsMediaCardHeadingStyles}>
							<CosmosRichText variant="headingSM" html={title?.html} />
						</CosmosCard.Link>
						<CosmosRichText
							variant="paragraphMD"
							color="eds.text.secondary"
							html={desc}
							className={newsMediaCardParagraphStyles}
						/>
					</VStack>
					<CosmosText
						variant="uiSM"
						color="eds.text.primary"
						text={author || ""}
						className={css({ fontWeight: "medium" })}
					/>
				</VStack>
			</CosmosCard.Content>
		</CosmosCard>
	);
}
